import React, { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
    Box, Button, CircularProgress, Stack, Typography,
    Dialog, DialogTitle, DialogContent, DialogActions, List, ListItemButton, ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
    DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext, rectSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../../../context/AuthContext';
import { UserType } from '../../../interfaces/userType.enum';
import { ModuleKey, widgetsForRole, isWidgetVisibleToRole } from '../registry';
import { isGatedModule } from '../registry/moduleMeta';
import { useDashboardAccess } from '../useDashboardAccess';
import useDashboardLayout from './useDashboardLayout';
import WidgetCard from './WidgetCard';
import ModuleUpsellCard from './ModuleUpsellCard';

const SPAN: Record<string, number> = { sm: 4, md: 6, lg: 12 };

interface Props {
    institutionId: string;
    boardKey: string;
    moduleScope: ModuleKey;
    fallbackIds: string[];
    title: string;
    subtitle?: string;
}

function SortableWidget({ id, children }: { id: string; children: React.ReactNode }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    return (
        <Box
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            sx={{ height: '100%' }}
            style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}
        >
            {children}
        </Box>
    );
}

/**
 * A lean, customizable board for a single dashboard module's deep page.
 * Distinct from the home `DashboardBoard`: it omits the home-only chrome (role-preview toggle,
 * premium upgrade Alert, Action Center). It reuses the generalized `useDashboardLayout` for
 * per-board persistence (`boardKey`), the same 12-col dnd grid + `WidgetCard`s, an Add-widget
 * dialog scoped to `moduleScope`, and a Reset. A user whose role holds no registry widget in this
 * module, or whose institution hasn't enabled it, is redirected home or shown the upsell card.
 */
export default function ModuleBoard({ institutionId, boardKey, moduleScope, fallbackIds, title, subtitle }: Props) {
    const { user } = useAuth();
    const roles: UserType[] = useMemo(() => {
        const r = user?.roles;
        return (Array.isArray(r) ? r : r ? [r] : []) as UserType[];
    }, [user]);

    const { visibleModules, upsellModules, enabledModules, ready } = useDashboardAccess();
    const { order, widgets, add, remove, reorder, reset } = useDashboardLayout({
        institutionId, boardKey, fallbackIds, editable: true,
    });
    // Hide (rather than spin forever on) a widget the user may no longer see, while keeping it in
    // the persisted order so it returns if access is restored. Two independent gates:
    //   - module disabled for the institution. Ungated modules are never in `enabledModules`, so
    //     they must not be filtered out — see isGatedModule.
    //   - the user's role no longer passes the widget's own role gate. The Add-widget picker
    //     always applied this, but a layout persisted under a higher role did not, so a demoted
    //     user kept rendering widgets whose endpoints now 403.
    const visibleWidgets = useMemo(
        () => widgets.filter(def =>
            (!isGatedModule(def.module) || enabledModules.has(def.module))
            && isWidgetVisibleToRole(def, roles)),
        [widgets, enabledModules, roles],
    );
    const [addOpen, setAddOpen] = useState(false);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

    // Addable = role-eligible widgets in THIS module that aren't already on the board.
    const addable = useMemo(
        () => widgetsForRole(roles, Array.from(enabledModules)).filter(w => w.module === moduleScope && !order.includes(w.id)),
        [roles, moduleScope, order, enabledModules],
    );

    const onDragEnd = (e: DragEndEvent) => {
        if (!e.over || e.active.id === e.over.id) return;
        const from = order.indexOf(String(e.active.id));
        const to = order.indexOf(String(e.over.id));
        if (from >= 0 && to >= 0) reorder(from, to);
    };

    // Wait for both the premium check and the enabled-modules fetch to resolve before judging
    // access: on first render `enabledModules` fails open to the full gated set and an owner-
    // capable user's premium check is still pending, so redirecting/upselling on that pending
    // state either bounces a valid visit home (the f71f847 bug) or flashes a real board (with a
    // burst of 403s) before the upsell for a module-disabled institution.
    if (!ready) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>;
    }

    // Role-eligible but the institution hasn't enabled this module: show the upsell, not a redirect.
    if (upsellModules.has(moduleScope)) {
        return <ModuleUpsellCard module={moduleScope} />;
    }

    // A user who cannot see this module at all must not reach its board by URL. Hooks above run
    // unconditionally, per the rules of hooks.
    if (!visibleModules.has(moduleScope)) {
        return <Navigate to="/" replace />;
    }

    return (
        <Box sx={{ p: 2, minWidth: 0 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>{title}</Typography>
            {subtitle && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{subtitle}</Typography>
            )}

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>Add widget</Button>
                <Button size="small" startIcon={<RestartAltIcon />} onClick={reset}>Reset to default</Button>
            </Stack>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={visibleWidgets.map(w => w.id)} strategy={rectSortingStrategy}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}>
                        {visibleWidgets.map(def => (
                            <Box key={def.id} sx={{ gridColumn: { xs: 'span 12', md: `span ${SPAN[def.size]}` } }}>
                                <SortableWidget id={def.id}>
                                    <WidgetCard def={def} institutionId={institutionId} editable onRemove={() => remove(def.id)} />
                                </SortableWidget>
                            </Box>
                        ))}
                    </Box>
                </SortableContext>
            </DndContext>

            <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="xs" closeAfterTransition={false}>
                <DialogTitle>Add a widget</DialogTitle>
                <DialogContent dividers>
                    {addable.length === 0
                        ? <Typography variant="body2" color="text.secondary">Every {moduleScope} widget available to you is already on the board.</Typography>
                        : (
                            <List>
                                {addable.map(w => (
                                    <ListItemButton key={w.id} onClick={() => { add(w.id); setAddOpen(false); }}>
                                        <ListItemText primary={w.title} secondary={w.module} />
                                    </ListItemButton>
                                ))}
                            </List>
                        )}
                </DialogContent>
                <DialogActions><Button onClick={() => setAddOpen(false)}>Close</Button></DialogActions>
            </Dialog>
        </Box>
    );
}
